import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Menu,
  MenuItem,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";

const DrawerList = ({
  pages,
  mobileOpen,
  onRouteClick,
  openStates,
  handleToggle,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState(null);
  const [activeMenuItems, setActiveMenuItems] = useState([]);
  const [activeSubmenuItems, setActiveSubmenuItems] = useState([]);

  const location = useLocation();
  const isActive = (route) => location.pathname === `/vlp/${route}`;

  const handleMenuClick = (event, subItems) => {
    setAnchorEl(event.currentTarget);
    setActiveMenuItems(subItems);
    setSubmenuAnchorEl(null);
    setActiveSubmenuItems([]);
  };

  const handleSubmenuClick = (event, subItems) => {
    setSubmenuAnchorEl(event.currentTarget);
    setActiveSubmenuItems(subItems);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSubmenuAnchorEl(null);
    setActiveMenuItems([]);
    setActiveSubmenuItems([]);
  };

  const renderListItems = (items, level = 0) =>
    items.map((item) => {
      const key = item.route || item.label;

      if (item.subItems) {
        const isOpen = openStates[item.label];

        if (!mobileOpen) {
          // Collapsed drawer - show as dropdown menu
          return (
            <React.Fragment key={key}>
              <ListItem
                button
                aria-label={item.label}
                onClick={(e) => handleMenuClick(e, item.subItems)}
                sx={{ pl: 4 * level }}
              >
                <ListItemIcon sx={{ color: "white", pl: 1 }}>
                  {item.icon}
                </ListItemIcon>
              </ListItem>

              {/* Top-level dropdown menu */}
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                {activeMenuItems.map((subItem) => (
                  <React.Fragment key={subItem.route || subItem.label}>
                    {subItem.subItems ? (
                      <MenuItem onClick={(e) => handleSubmenuClick(e, subItem.subItems)}>
                        <ListItemIcon>{subItem.icon}</ListItemIcon>
                        {subItem.label}
                        <ArrowRightIcon />
                      </MenuItem>
                    ) : (
                      <MenuItem
                        onClick={() => {
                          onRouteClick(subItem.route);
                          handleMenuClose();
                        }}
                      >
                        <ListItemIcon>{subItem.icon}</ListItemIcon>
                        {subItem.label}
                      </MenuItem>
                    )}
                  </React.Fragment>
                ))}
              </Menu>

              {/* Nested submenu */}
              <Menu
                anchorEl={submenuAnchorEl}
                open={Boolean(submenuAnchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
              >
                {activeSubmenuItems.map((subSubItem) => (
                  <MenuItem
                    key={subSubItem.route || subSubItem.label}
                    onClick={() => {
                      onRouteClick(subSubItem.route);
                      handleMenuClose();
                    }}
                  >
                    <ListItemIcon>{subSubItem.icon}</ListItemIcon>
                    {subSubItem.label}
                  </MenuItem>
                ))}
              </Menu>
            </React.Fragment>
          );
        }

        // Expanded drawer - use Collapse for nested items
        return (
          <React.Fragment key={key}>
            <ListItem
              button
              aria-label={item.label}
              onClick={() => handleToggle(item.label)}
              sx={{ pl: 4 * level }}
            >
              <ListItemIcon sx={{ color: "white", pl: 1 }}>{item.icon}</ListItemIcon>
              {mobileOpen && (
                <>
                  <ListItemText primary={item.label} />
                  {isOpen ? <ExpandLess /> : <ExpandMore />}
                </>
              )}
            </ListItem>

            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {renderListItems(item.subItems, level + 1)}
              </List>
            </Collapse>
          </React.Fragment>
        );
      }

      // Leaf item
      const active = isActive(item.route);
      return (
        <ListItem
          key={key}
          button
          aria-label={item.label}
          onClick={() => onRouteClick(item.route)}
          sx={{
            pl: 4 * level,
            backgroundColor: active ? "rgba(255,255,255,0.2)" : "transparent",
            borderLeft: active ? "3px solid white" : "3px solid transparent",
            borderRadius: 0,
            "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
          }}
        >
          <ListItemIcon sx={{ color: "white", pl: active ? "5px" : 1 }}>
            {item.icon}
          </ListItemIcon>
          {mobileOpen && (
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{ fontWeight: active ? 600 : 400 }}
            />
          )}
        </ListItem>
      );
    });

  return <List>{renderListItems(pages)}</List>;
};

export default DrawerList;